import {
    type ApolloError,
    type ApolloQueryResult,
    type OperationVariables,
    type TypedDocumentNode,
    useApolloClient,
} from '@apollo/client';
import { cluster, unique } from 'radash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MAX_PAGE_SIZE } from '@api/pagination';
import { appendNodes, type NodesOf } from './useQueryConnection';

export interface ChunkedQueryConnection<
    Response,
    Field extends keyof Response,
> {
    nodes: NodesOf<Response[Field]>[number][];
    loading: boolean;
    error: ApolloError | undefined;
}

export interface ChunkedQueryOptions<Field> {
    /** Root connection field of the query response whose nodes are collected. */
    rootField: Field;
    skip?: boolean;
    chunkSize?: number;
}

interface ChunkResult<Response> {
    data: Response | undefined;
    loading: boolean;
    error: ApolloError | undefined;
}

interface ChunkedState<Response> {
    signature: string;
    results: (ChunkResult<Response> | undefined)[];
}

/**
 * Watches `query` once per chunk of `ids` (at most `MAX_PAGE_SIZE` ids each)
 * and concatenates the nodes of the `rootField` connection of every result.
 * Like `useQuery`, results follow the Apollo cache, so cache writes to a node
 * are reflected without a refetch. `getVariables` must give the variables for
 * one chunk, including `first`.
 *
 * Resubscription: only `ids`, `chunkSize`, `skip` and the `query` document
 * start new watches. Every other input of `getVariables` (filters, flags) is
 * read from a ref when the subscriptions are created, so changing it alone
 * does not refetch. Fold such an input into `ids` (or remount the consumer)
 * if it must trigger a new request. When the signature changes, results of
 * the previous watches are dropped rather than shown for the new ids.
 *
 * Failure contract: a failed chunk contributes no nodes, nodes of the chunks
 * that succeeded are still returned, and `error` is the first failure by chunk
 * order. Consumers that need the complete set must treat a defined `error` as
 * an incomplete result.
 */
export function useChunkedQueryConnection<
    Response,
    Payload extends OperationVariables,
    Field extends keyof Response,
>(
    query: TypedDocumentNode<Response, Payload>,
    ids: string[],
    getVariables: (chunk: string[]) => Payload,
    {
        rootField,
        skip = false,
        chunkSize = MAX_PAGE_SIZE,
    }: ChunkedQueryOptions<Field>,
): ChunkedQueryConnection<Response, Field> {
    const client = useApolloClient();
    const getVariablesRef = useRef(getVariables);
    getVariablesRef.current = getVariables;
    // Duplicate ids would request and render the same node twice.
    const uniqueIds = unique(ids);
    const idsRef = useRef(uniqueIds);
    idsRef.current = uniqueIds;

    // JSON keeps ids containing separators from colliding with other id lists.
    const signature = skip ? '' : `${chunkSize}:${JSON.stringify(uniqueIds)}`;
    const [state, setState] = useState<ChunkedState<Response>>({
        signature,
        results: [],
    });

    useEffect(() => {
        if (signature === '') {
            return undefined;
        }

        const chunks = cluster(idsRef.current, chunkSize);
        const update = (index: number, result: ChunkResult<Response>): void => {
            setState((previous) => {
                const results =
                    previous.signature === signature ? previous.results : [];
                const current = results[index];
                if (
                    current &&
                    current.data === result.data &&
                    current.loading === result.loading &&
                    current.error === result.error
                ) {
                    return previous;
                }
                const next = [...results];
                next[index] = result;
                return { signature, results: next };
            });
        };

        const subscriptions = chunks.map((chunk, index) =>
            client
                .watchQuery({
                    query,
                    variables: getVariablesRef.current(chunk),
                })
                .subscribe({
                    next: (result: ApolloQueryResult<Response>) => {
                        update(index, {
                            data: result.data,
                            loading: result.loading,
                            error: undefined,
                        });
                    },
                    error: (error: ApolloError) => {
                        update(index, {
                            data: undefined,
                            loading: false,
                            error,
                        });
                    },
                }),
        );

        return () => {
            subscriptions.forEach((subscription) => {
                subscription.unsubscribe();
            });
        };
    }, [client, query, signature]);

    return useMemo(() => {
        if (signature === '') {
            return { nodes: [], loading: false, error: undefined };
        }

        const results = state.signature === signature ? state.results : [];
        const chunkCount = Math.ceil(uniqueIds.length / chunkSize);
        let loading = false;
        let error: ApolloError | undefined;
        const nodes: ChunkedQueryConnection<Response, Field>['nodes'] = [];

        for (let index = 0; index < chunkCount; index += 1) {
            const result = results[index];
            if (!result || result.loading) {
                loading = true;
            }
            error ??= result?.error;
            if (result?.data) {
                appendNodes<Response[Field]>(nodes, result.data[rootField]);
            }
        }

        return { nodes, loading, error };
    }, [chunkSize, uniqueIds.length, rootField, signature, state]);
}
